import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export const AUDIENCES = [
  ["all", "Everyone in the organization"],
  ["managers", "HR Managers only"],
  ["reviewers", "HR Managers + assigned reviewers"],
  ["private", "Private — uploader + selected people"],
];

// Upload/import form field (managers). `form.audience` defaults to "all".
export function AudienceSelect({ form, setForm, prefix }) {
  return (
    <div>
      <Label>Who can see this document</Label>
      <Select value={form.audience || "all"} onValueChange={(v) => setForm({ ...form, audience: v })}>
        <SelectTrigger data-testid={`${prefix}-audience`} className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>{AUDIENCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

// Inline audience editor in the document dialog (managers, non-seeded docs only).
export default function DocumentAudience({ doc, onChanged }) {
  const [audience, setAudience] = useState(doc.audience || "all");
  const [allowed, setAllowed] = useState(doc.allowed_user_ids || []);
  const [people, setPeople] = useState([]);
  useEffect(() => { if (audience === "private" && people.length === 0) api.get("/team").then((r) => setPeople(r.data.users)).catch(() => {}); }, [audience, people.length]);

  const save = async (a, ids) => {
    try {
      const { data } = await api.patch(`/documents/${doc.id}/audience`, { audience: a, allowed_user_ids: ids });
      onChanged(data); toast.success("Access updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update access"); }
  };
  const change = (a) => { setAudience(a); if (a !== "private") save(a, []); };
  const toggle = (id) => { const ids = allowed.includes(id) ? allowed.filter((x) => x !== id) : [...allowed, id]; setAllowed(ids); save("private", ids); };

  if (doc.is_seed) return null;
  return (
    <div data-testid="doc-audience" className="mt-2 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold"><Lock className="w-3 h-3" /> Access</span>
      <Select value={audience} onValueChange={change}>
        <SelectTrigger data-testid="doc-audience-select" className="h-7 w-72 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{AUDIENCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      {audience === "private" && (
        <div data-testid="doc-allowed-users" className="w-full flex flex-wrap gap-2 mt-1">
          {people.filter((p) => p.id !== doc.uploaded_by).map((p) => (
            <label key={p.id} className="flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-slate-200"><Checkbox data-testid={`allow-${p.id}`} checked={allowed.includes(p.id)} onCheckedChange={() => toggle(p.id)} />{p.name}</label>
          ))}
        </div>
      )}
    </div>
  );
}
